/**
 * PATH: packages/ui/src/components/SettingsModal.tsx
 * PURPOSE: Universal settings modal for all hubs — clean, minimal, Notion-inspired.
 *
 * DESIGN PRINCIPLES (v2):
 *   - Sidebar shows only 5-6 items, no category headers needed
 *   - No redundant breadcrumb bar — just the content
 *   - Wider content area, narrower sidebar
 *   - Smooth transitions
 */

import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react'
import { X, Search, Keyboard, Lock, Settings, type LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'

// Direct ESM imports
import { LockedTabContent } from './settings/LockedTabContent'
import { isUserAdmin as isUserAdminUtil } from './settings/utils'

// ============================================================================
// TYPES
// ============================================================================

export interface SettingsTab {
  id: string
  label: string
  icon: LucideIcon
  content: ReactNode
  category: string
  description?: string
  badge?: string
  adminOnly?: boolean
  /** Granular RBAC permission requirement (takes precedence over adminOnly) */
  requiredPermission?: { module: string; action: string }
}

export interface SettingsUser {
  id?: string
  first_name?: string
  last_name?: string
  email?: string
  isAdmin?: boolean
  role?: string
  permissions?: string[] | Record<string, { action: string; scope?: string }>
  hierarchy_level?: number
  can_manage_settings?: boolean
  organisation_name?: string
  organisation_id?: string
  avatar_url?: string
}

const isUserAdmin = isUserAdminUtil

export interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  tabs: SettingsTab[]
  defaultTab?: string
  user?: SettingsUser | null
  className?: string
  /** Leave out tabs the person cannot open instead of listing them padlocked
   *  (field crew saw every admin tab with a lock). */
  hideLockedTabs?: boolean
}

// ============================================================================
// CONTEXT
// ============================================================================

interface SettingsModalContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
  activeTab: string
  setActiveTab: (tab: string) => void
}

const SettingsModalContext = createContext<SettingsModalContextValue | null>(null)

export function SettingsModalProvider({
  children,
  // 'profile' is the canonical landing tab in the flat layout — it
  // is always present (first Account-category tab) and matches the
  // TAB_ID_MAP target. Pre-2026-05-15 this defaulted to 'account',
  // the id of the (now-removed) consolidated wrapper tab.
  defaultTab = 'profile',
}: {
  children: ReactNode
  defaultTab?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(defaultTab)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen(prev => !prev), [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggle])

  return (
    <SettingsModalContext.Provider value={{ isOpen, open, close, toggle, activeTab, setActiveTab }}>
      {children}
    </SettingsModalContext.Provider>
  )
}

export function useSettingsModal() {
  const context = useContext(SettingsModalContext)

  if (!context) {
    const [isOpen, setIsOpen] = useState(false)
    // 'profile' — see SettingsModalProvider default-tab comment above.
    const [activeTab, setActiveTab] = useState('profile')

    return {
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen(prev => !prev),
      activeTab,
      setActiveTab,
    }
  }

  return context
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SettingsModal({
  isOpen,
  onClose,
  tabs,
  // 'profile' — flat-layout canonical landing tab (see provider above).
  defaultTab = 'profile',
  user,
  className,
  hideLockedTabs = false,
}: SettingsModalProps) {
  const [activeSection, setActiveSection] = useState(defaultTab)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (isOpen) {
      setActiveSection(defaultTab)
      setSearchQuery('')
    }
  }, [isOpen, defaultTab])

  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const userIsAdmin = isUserAdmin(user)

  const ACTION_LEVELS: Record<string, number> = {
    none: 0, read: 1, write: 2, delete: 3, admin: 4,
  }

  const isTabLocked = (tab: SettingsTab): boolean => {
    if (tab.requiredPermission && user?.permissions && typeof user.permissions === 'object' && !Array.isArray(user.permissions)) {
      const perms = user.permissions as Record<string, { action: string; scope?: string }>
      const modulePerm = perms[tab.requiredPermission.module]
      if (!modulePerm) return !userIsAdmin
      const userLevel = ACTION_LEVELS[modulePerm.action] ?? 0
      const requiredLevel = ACTION_LEVELS[tab.requiredPermission.action] ?? 0
      return userLevel < requiredLevel
    }
    return !!(tab.adminOnly && !userIsAdmin)
  }

  const openableTabs = hideLockedTabs ? tabs.filter(tab => !isTabLocked(tab)) : tabs

  // Filter by search
  const filteredTabs = openableTabs.filter(tab => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      tab.label.toLowerCase().includes(q) ||
      tab.description?.toLowerCase().includes(q) ||
      tab.category.toLowerCase().includes(q)
    )
  })

  // Group by category
  const groupedTabs = filteredTabs.reduce((acc, tab) => {
    if (!acc[tab.category]) acc[tab.category] = []
    acc[tab.category].push(tab)
    return acc
  }, {} as Record<string, SettingsTab[]>)

  // A hidden tab asked for by id lands on the first tab the person can open.
  const activeTab = openableTabs.find(t => t.id === activeSection) ?? (hideLockedTabs ? openableTabs[0] : undefined)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-6 pointer-events-none">
        <div
          className={cn(
            'pointer-events-auto overflow-hidden',
            'bg-white dark:bg-slate-950 shadow-2xl',
            'border border-slate-200/80 dark:border-slate-800',
            // Mobile (<sm): full-height sheet, vertically STACKED — the nav
            // becomes a horizontal strip above the content so the content
            // pane gets the full width instead of being crushed to ~100px
            // by a fixed left sidebar (the "one word per line" bug).
            'flex flex-col w-full h-full rounded-xl',
            // Desktop (sm+): Notion-style wide panel with a fixed left
            // sidebar. max-w-7xl (1280px) gives the org/entities tables and
            // the 2-col integrations grid room; w-[96vw] keeps it near-bleed.
            'sm:flex-row sm:w-[96vw] sm:max-w-7xl sm:h-[88vh] sm:rounded-2xl',
            className
          )}
        >
          {/* ── Mobile top bar: brand + search + horizontal tab strip ── */}
          <div className="sm:hidden flex-shrink-0 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/60">
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-sm">
                  <Settings className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="font-semibold text-slate-900 dark:text-white text-sm">Settings</span>
              </div>
              <button
                onClick={onClose}
                aria-label="Close settings"
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 pb-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search settings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn(
                    'w-full h-9 pl-8 pr-3 rounded-lg text-sm',
                    'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700',
                    'text-slate-900 dark:text-white placeholder-slate-400',
                    'focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500'
                  )}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="flex items-center gap-1 px-3 pb-2 min-w-max">
                {filteredTabs.map((tab) => {
                  const Icon = tab.icon
                  const isActive = activeTab?.id === tab.id
                  const locked = isTabLocked(tab)
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSection(tab.id)}
                      aria-disabled={locked}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0',
                        isActive
                          ? 'bg-teal-500 text-white shadow-sm'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
                        locked && !isActive && 'opacity-60'
                      )}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {tab.label}
                      {locked && <Lock className="w-3 h-3" />}
                    </button>
                  )
                })}
                {filteredTabs.length === 0 && (
                  <span className="px-3 py-2 text-xs text-slate-400">
                    No settings match "{searchQuery}"
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Sidebar (desktop) ── */}
          <div className="hidden sm:flex w-56 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 flex-col bg-slate-50/70 dark:bg-slate-900/60">
            {/* Sidebar header */}
            <div className="px-4 pt-5 pb-3 flex-shrink-0">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-sm">
                  <Settings className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-slate-900 dark:text-white text-[15px]">Settings</span>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn(
                    'w-full h-8 pl-8 pr-3 rounded-lg text-xs',
                    'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700',
                    'text-slate-900 dark:text-white placeholder-slate-400',
                    'focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500',
                    'transition-colors'
                  )}
                />
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto px-2 pb-3">
              {Object.entries(groupedTabs).map(([category, categoryTabs]) => (
                <div key={category} className="mb-3">
                  <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                    {category}
                  </div>
                  <div className="mt-0.5 space-y-0.5">
                    {categoryTabs.map((tab) => {
                      const Icon = tab.icon
                      const isActive = activeTab?.id === tab.id
                      const locked = isTabLocked(tab)
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveSection(tab.id)}
                          aria-disabled={locked}
                          title={locked ? `${tab.label} (Admin only)` : tab.description || tab.label}
                          className={cn(
                            'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-[13px] transition-all duration-150',
                            isActive
                              ? locked
                                ? 'bg-slate-200/60 dark:bg-slate-800 text-slate-500'
                                : 'bg-teal-500/10 text-teal-700 dark:text-teal-300 shadow-sm'
                              : locked
                                ? 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800/50 opacity-60'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/70'
                          )}
                        >
                          <Icon className={cn(
                            'w-4 h-4 flex-shrink-0',
                            isActive && !locked ? 'text-teal-500' : ''
                          )} />
                          <span className="font-medium truncate">{tab.label}</span>
                          {locked && <Lock className="ml-auto w-3 h-3 text-slate-400 flex-shrink-0" />}
                          {!locked && tab.badge && (
                            <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300 font-medium">
                              {tab.badge}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}

              {filteredTabs.length === 0 && (
                <div className="px-3 py-8 text-center text-xs text-slate-400">
                  No settings match "{searchQuery}"
                </div>
              )}
            </nav>

            {/* Sidebar footer */}
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex-shrink-0">
              <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                <Keyboard className="w-3 h-3" />
                <span>⌘ , to toggle</span>
              </div>
            </div>
          </div>

          {/* ── Content ── */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* Content header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
              <div className="flex items-center gap-3">
                {activeTab && (
                  <>
                    <activeTab.icon className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                    <div>
                      <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white leading-tight">
                        {activeTab.label}
                      </h2>
                      {activeTab.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{activeTab.description}</p>
                      )}
                    </div>
                    {isTabLocked(activeTab) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                        <Lock className="w-2.5 h-2.5" />
                        Admin
                      </span>
                    )}
                  </>
                )}
              </div>
              {/* Desktop close — mobile uses the close button in the top bar */}
              <button
                onClick={onClose}
                className="hidden sm:block p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {activeTab && isTabLocked(activeTab) ? (
                <LockedTabContent tabId={activeTab.id} tabLabel={activeTab.label} />
              ) : (
                activeTab?.content
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsModal
